import React, { useState, useCallback } from 'react';
import { Spinner, Button } from 'react-bootstrap';
import { Upload } from 'lucide-react';
import { ProjectModel } from '../../../types/projects';
import { PhotoModel } from '../../../types/protocols';
import { ProjectPhotoType } from '../../../Helpers/ProjectPhotoUtils';
import PhotoGallery from '../../../Components/PhotoGallery';
import { UploadPickerModal } from '../../../Components/UploadPickerModal';

interface ProjectPhotosSectionProps {
    project: ProjectModel;
    projectKey: string;
    onUploadPhotos: (photoType: ProjectPhotoType, files: File[]) => Promise<void>;
    onDeletePhoto: (photoType: ProjectPhotoType, photoKey: string) => Promise<void>;
    submitting?: boolean;
    /**
     * Which photo buckets to render. Defaults to all three (before/progress/after).
     * Callers (e.g. work orders) pass only the enabled buckets; the Före/Pågående/
     * Efter labels are kept even when a single bucket is shown.
     */
    photoTypes?: ProjectPhotoType[];
    /**
     * When true, renders a read-only view: hides the upload label/input,
     * disables drag-and-drop upload, and hides the per-photo delete control.
     * Thumbnails stay visible and openable in the viewer, and the Före/
     * Pågående/Efter group labels are kept. Defaults to false so the Projects
     * pages behave exactly as before.
     */
    readOnly?: boolean;
}

interface PhotoCategoryProps {
    title: string;
    photoType: ProjectPhotoType;
    photos: { [key: string]: PhotoModel } | undefined;
    buildingKey: string;
    onUpload: (files: File[]) => Promise<void>;
    onDelete: (photoKey: string) => Promise<void>;
    uploading: boolean;
    dragOver: boolean;
    onDragOver: () => void;
    onDragLeave: () => void;
    onDrop: (files: File[]) => void;
    readOnly?: boolean;
}

const PhotoCategory: React.FC<PhotoCategoryProps> = ({
    title,
    photoType,
    photos,
    buildingKey,
    onUpload,
    onDelete,
    uploading,
    dragOver,
    onDragOver,
    onDragLeave,
    onDrop,
    readOnly = false,
}) => {
    const [showUploadModal, setShowUploadModal] = useState(false);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver();
    }, [onDragOver]);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            onDragLeave();
        }
    }, [onDragLeave]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onDragLeave();

        const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
        if (files.length > 0) {
            onDrop(files);
        }
    }, [onDragLeave, onDrop]);

    const handlePickedFiles = useCallback((files: File[]) => {
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        if (imageFiles.length > 0) {
            onUpload(imageFiles);
        }
    }, [onUpload]);

    const photoList = photos ? Object.values(photos) : [];

    return (
        <div style={{ marginBottom: '20px', border: '1px solid #dee2e6', borderRadius: '10px', backgroundColor: '#fff' }}>
            <div style={{
                backgroundColor: '#f8f9fa',
                padding: '12px 16px',
                borderBottom: '1px solid #dee2e6',
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h6 style={{ margin: 0 }}>{title}</h6>
                    {!readOnly && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setShowUploadModal(true)}
                        >
                            <Upload size={14} className="me-1" />
                            Ladda upp
                        </Button>
                    )}
                </div>
            </div>
            <div
                onDragOver={readOnly ? undefined : handleDragOver}
                onDragLeave={readOnly ? undefined : handleDragLeave}
                onDrop={readOnly ? undefined : handleDrop}
                style={{
                    border: dragOver ? '2px dashed #007bff' : '2px dashed transparent',
                    borderRadius: '8px',
                    backgroundColor: dragOver ? '#f0f8ff' : 'transparent',
                    transition: 'all 0.3s ease',
                    padding: dragOver ? '22px' : '24px',
                    minHeight: '100px',
                    position: 'relative'
                }}
            >
                {uploading && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        padding: '20px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <Spinner animation="border" size="sm" />
                        <span>Laddar upp bilder...</span>
                    </div>
                )}
                {photoList.length > 0 ? (
                    <PhotoGallery
                        photos={photos ?? {}}
                        buildingKey={buildingKey}
                        thumbnailSize={100}
                        showDate
                        showDeleteButton={!readOnly}
                        onDeleteClick={(photo) => onDelete(photo.key)}
                    />
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: dragOver ? '20px' : '40px',
                        color: dragOver ? '#007bff' : '#666',
                        fontStyle: 'italic',
                        transition: 'all 0.3s ease'
                    }}>
                        {dragOver ? (
                            <div>
                                <div style={{ fontSize: '32px', marginBottom: '10px' }}>📁</div>
                                <div>Släpp bilder här</div>
                            </div>
                        ) : (
                            <div>
                                <img
                                    src="/Images/UI/camera.png"
                                    alt="Camera"
                                    style={{
                                        width: '35px',
                                        height: 'auto',
                                        display: 'block',
                                        margin: '0 auto 8px auto'
                                    }}
                                />
                                <div>
                                    {readOnly
                                        ? 'Inga bilder.'
                                        : 'Inga bilder ännu. Dra och släpp bilder här eller klicka på "Ladda upp".'}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!readOnly && (
                <UploadPickerModal
                    show={showUploadModal}
                    onHide={() => setShowUploadModal(false)}
                    onFilesSelected={handlePickedFiles}
                    accept="image/*"
                    title={`Ladda upp – ${title}`}
                    multiple
                />
            )}
        </div>
    );
};

export const ProjectPhotosSection: React.FC<ProjectPhotosSectionProps> = ({
    project,
    projectKey,
    onUploadPhotos,
    onDeletePhoto,
    submitting = false,
    photoTypes,
    readOnly = false,
}) => {
    const [dragOverType, setDragOverType] = useState<ProjectPhotoType | null>(null);
    const [uploadingType, setUploadingType] = useState<ProjectPhotoType | null>(null);

    const handleUpload = useCallback(async (photoType: ProjectPhotoType, files: File[]) => {
        setUploadingType(photoType);
        try {
            await onUploadPhotos(photoType, files);
        } finally {
            setUploadingType(null);
        }
    }, [onUploadPhotos]);

    const allPhotoCategories: { type: ProjectPhotoType; title: string }[] = [
        { type: 'beforePictures', title: 'Före-bilder' },
        { type: 'progressPictures', title: 'Pågående-bilder' },
        { type: 'afterPictures', title: 'Efter-bilder' },
    ];

    // Restrict to the requested buckets (e.g. work orders render only the
    // enabled ones). Keep the canonical order and labels.
    const photoCategories = photoTypes
        ? allPhotoCategories.filter(({ type }) => photoTypes.includes(type))
        : allPhotoCategories;

    return (
        <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #dee2e6'
        }}>
            <h5 style={{ marginBottom: '16px' }}>Bilder</h5>
            {photoCategories.map(({ type, title }) => (
                <PhotoCategory
                    key={type}
                    title={title}
                    photoType={type}
                    photos={project[type]}
                    buildingKey={projectKey}
                    onUpload={(files) => handleUpload(type, files)}
                    onDelete={(photoKey) => onDeletePhoto(type, photoKey)}
                    uploading={uploadingType === type}
                    dragOver={dragOverType === type}
                    onDragOver={() => setDragOverType(type)}
                    onDragLeave={() => setDragOverType(null)}
                    onDrop={(files) => handleUpload(type, files)}
                    readOnly={readOnly}
                />
            ))}
        </div>
    );
};
